import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useCreateScheduledMeetingProposal } from "@/hooks/useScheduledMeetings";
import { supabase } from "@/integrations/supabase/client";
import { TIMEZONE_OPTIONS, timezoneLabel } from "@/lib/timezones";
import { useAuth } from "@/lib/auth";
import { Loader2, Calendar as CalendarIcon, Clock, Plus, X, Globe, AlertCircle, Bug } from "lucide-react";
import { format, addMinutes, setHours, setMinutes, isBefore, startOfToday, isToday } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { toast } from "sonner";

interface ScheduleMeetingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mentorshipRequestId: string;
  threadId: string;
  otherPersonName: string;
}

interface TimeSlot {
  id: string;
  date: Date;
  time: string;
}

const DURATION_OPTIONS = [
  { value: "15", label: "15 min" },
  { value: "30", label: "30 min" },
  { value: "45", label: "45 min" },
  { value: "60", label: "60 min" },
];

const TIME_OPTIONS = Array.from({ length: 28 }, (_, i) => {
  const hour = Math.floor(i / 2) + 8; // 8 AM to 9 PM
  const minute = (i % 2) * 30;
  const time24 = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  const date = setMinutes(setHours(new Date(), hour), minute);
  const time12 = format(date, "h:mm a");
  return { value: time24, label: time12 };
});


export function ScheduleMeetingDialog({
  open,
  onOpenChange,
  mentorshipRequestId,
  threadId,
  otherPersonName,
}: ScheduleMeetingDialogProps) {
  const { user } = useAuth();
  const [duration, setDuration] = useState("30");
  const [timezone, setTimezone] = useState("America/Jamaica");
  const [note, setNote] = useState("");
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showDebugPayload, setShowDebugPayload] = useState(false);
  
  // User-visible status feedback for debugging proposal flow
  const [statusMessage, setStatusMessage] = useState<{
    text: string;
    type: "info" | "loading" | "success" | "error";
  } | null>(null);

  const createProposal = useCreateScheduledMeetingProposal();

  // Minimum buffer: 5 minutes from now
  const MIN_BUFFER_MINUTES = 5;

  // Compute the slot date/time in UTC based on selected timezone
  // CRITICAL: This converts user-selected DATE + TIME in their chosen TIMEZONE to UTC
  // The result is stored in DB as timestamptz and is the source of truth for Zoom
  const getSlotDateUTC = (): Date | null => {
    if (!selectedDate || !selectedTime) return null;
    
    const [hours, minutes] = selectedTime.split(":").map(Number);
    // Build local date string from user's selected calendar date + time picker value
    // This represents what the user sees and wants: "January 20, 2026 at 7:00 PM Jamaica time"
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const hour = String(hours).padStart(2, "0");
    const min = String(minutes).padStart(2, "0");
    
    // Parse as if the date/time is in the selected timezone, then convert to UTC
    // fromZonedTime("2026-01-20T19:00:00", "America/Jamaica") => Date in UTC representing 7pm Jamaica time
    const localDateTimeString = `${year}-${month}-${day}T${hour}:${min}:00`;
    const utcDate = fromZonedTime(localDateTimeString, timezone);
    
    // Instrumentation: Log the conversion for debugging
    console.log('[ScheduleMeetingDialog] Time conversion (user selection → UTC):', {
      userSelectedDate: format(selectedDate, "yyyy-MM-dd"),
      userSelectedTime: selectedTime,
      userSelectedTimezone: timezone,
      localDateTimeString,
      resultUtcIso: utcDate.toISOString(),
      // Verify by showing what time this represents in user's timezone
      verifyInUserTz: formatInTimeZone(utcDate, timezone, "yyyy-MM-dd HH:mm:ss zzz"),
    });
    
    return utcDate;
  };

  // Validate the current selection
  const getValidationError = (): string | null => {
    if (!selectedDate || !selectedTime) return null;
    
    const slotDateUTC = getSlotDateUTC();
    if (!slotDateUTC) return null;
    
    const now = new Date();
    const minValidTime = addMinutes(now, MIN_BUFFER_MINUTES);
    
    if (isBefore(slotDateUTC, now)) {
      return "Please choose a time in the future.";
    }
    
    if (isBefore(slotDateUTC, minValidTime)) {
      return "Please choose a time at least 5 minutes from now.";
    }
    
    // Check for duplicate
    const exists = slots.some(
      (s) => s.date.getTime() === slotDateUTC.getTime()
    );
    if (exists) {
      return "This time slot is already added.";
    }
    
    return null;
  };

  const validationError = getValidationError();
  const canAddSlot = selectedDate && selectedTime && !validationError && slots.length < 5;

  const handleAddSlot = async () => {
    if (!canAddSlot) return;

    const slotDateUTC = getSlotDateUTC();
    if (!slotDateUTC) return;

    const slotId = crypto.randomUUID();
    const durationMinutes = parseInt(duration);
    
    // Log client_add_slot debug event for pipeline tracing
    try {
      // Previously this re-imported supabase (shadowing the module-level import)
      // and "resolved" the user via a ternary whose BOTH branches returned
      // { user: null }, so user_id was never recorded.
      await supabase.from("scheduling_debug_events").insert({
        stage: "client_add_slot",
        user_id: user?.id ?? null,
        thread_id: threadId || null,
        mentorship_request_id: mentorshipRequestId || null,
        timezone,
        payload: {
          user_selected_date: format(selectedDate!, "yyyy-MM-dd"),
          user_selected_time: selectedTime,
          user_timezone: timezone,
          computed_utc_iso: slotDateUTC.toISOString(),
          computed_end_utc_iso: addMinutes(slotDateUTC, durationMinutes).toISOString(),
          duration_minutes: durationMinutes,
          local_slot_id: slotId,
        },
      });
      console.log("[ScheduleMeetingDialog] Logged client_add_slot debug event");
    } catch (logErr) {
      console.warn("[ScheduleMeetingDialog] Failed to log debug event:", logErr);
    }

    setSlots([
      ...slots,
      {
        id: slotId,
        date: slotDateUTC,
        time: selectedTime,
      },
    ]);

    // Reset selection for next slot
    setSelectedDate(undefined);
    setSelectedTime("");
  };

  const handleRemoveSlot = (id: string) => {
    setSlots(slots.filter((s) => s.id !== id));
  };

  const handleSubmit = async () => {
    if (slots.length < 1) return;

    // STEP 1: Show immediate confirmation that button was clicked
    setStatusMessage({ text: "✓ Propose button clicked", type: "info" });

    // Clear previous error
    setSubmitError(null);

    const durationMinutes = parseInt(duration);
    const formattedSlots = slots.map((slot) => ({
      start_at: slot.date.toISOString(),
      end_at: addMinutes(slot.date, durationMinutes).toISOString(),
    }));

    // Build payload for logging
    const rpcPayload = {
      p_mentorship_request_id: mentorshipRequestId,
      p_thread_id: threadId,
      p_duration_minutes: durationMinutes,
      p_timezone: timezone,
      p_note: note.trim() || null,
      p_slots: formattedSlots,
    };

    // Instrumentation: Log the slots being submitted to RPC
    console.log('[ScheduleMeetingDialog] Submitting proposal with RPC payload:', rpcPayload);
    console.log('[ScheduleMeetingDialog] Slots with timezone verification:', {
      slots: formattedSlots.map((s, i) => ({
        slotIndex: i + 1,
        start_at_utc: s.start_at,
        end_at_utc: s.end_at,
        start_in_user_tz: formatInTimeZone(new Date(s.start_at), timezone, "yyyy-MM-dd HH:mm zzz"),
        end_in_user_tz: formatInTimeZone(new Date(s.end_at), timezone, "yyyy-MM-dd HH:mm zzz"),
      })),
    });

    // STEP 2: Show that we're about to submit
    // Small delay so user can see the "clicked" message first
    await new Promise(resolve => setTimeout(resolve, 300));
    setStatusMessage({ text: "⏳ Submitting meeting proposal...", type: "loading" });

    // Show loading toast
    const loadingToastId = toast.loading("Submitting proposal...");

    try {
      const result = await createProposal.mutateAsync({
        mentorshipRequestId,
        threadId,
        durationMinutes,
        timezone,
        note: note.trim() || undefined,
        slots: formattedSlots,
      });

      toast.dismiss(loadingToastId);
      
      // STEP 3a: Show success with IDs (RPC now returns meeting_id, proposal_id, slots_inserted)
      const meetingId = result?.meeting_id || "unknown";
      const proposalId = result?.proposal_id || "unknown";
      const slotsInserted = result?.slots_inserted || 0;
      
      setStatusMessage({ 
        text: `✓ Proposal created! Meeting: ${meetingId === "unknown" ? "unknown" : meetingId.slice(0, 8)}... Proposal: ${proposalId === "unknown" ? "unknown" : proposalId.slice(0, 8)}... Slots: ${slotsInserted}`, 
        type: "success" 
      });
      
      console.log("[ScheduleMeetingDialog] Proposal created:", { meetingId, proposalId, slotsInserted });
      
      // If RPC already reported slots inserted, we can trust that
      if (slotsInserted > 0) {
        setStatusMessage({ 
          text: `✓ RPC confirmed ${slotsInserted} slots inserted. Verifying via RPC...`, 
          type: "success" 
        });
      }
      
      // STEP 4: Verify slots using SECURITY DEFINER RPC (bypasses RLS issues)
      await new Promise(resolve => setTimeout(resolve, 500));
      setStatusMessage({ text: "⏳ Verifying slots via RPC...", type: "loading" });
      
      if (proposalId && proposalId !== "unknown") {
        try {
          // Use the verify_proposal_slots RPC for reliable verification
          // Cast to any because types may not be updated yet for new RPC
          const { data: verifyData, error: verifyError } = await (supabase.rpc as any)("verify_proposal_slots", {
            p_proposal_id: proposalId,
          });
          
          // Type the response
          const verifyResult = verifyData as { 
            success: boolean; 
            error?: string; 
            proposal_id?: string;
            meeting_id?: string;
            status?: string;
            slot_count?: number;
          } | null;
          
          if (verifyError) {
            console.error("[ScheduleMeetingDialog] Verify RPC error:", verifyError);
            setStatusMessage({ 
              text: `⚠ Verify RPC error: ${verifyError.code || "UNKNOWN"}: ${verifyError.message}`, 
              type: "error" 
            });
            toast.error(`Verify RPC error: ${verifyError.message}`);
            
            // Log detailed error
            await supabase.from("error_logs").insert([{
              error_message: "verify_proposal_slots RPC failed",
              error_details: JSON.stringify({ code: verifyError.code, message: verifyError.message, details: verifyError.details }),
              component: "scheduling_verification",
              action: "verify_slots",
              metadata: {
                threadId,
                mentorshipRequestId,
                meetingId,
                proposalId,
                verifyError,
              },
            }]);
          } else if (!verifyResult?.success) {
            const errorMsg = verifyResult?.error || "Unknown verification error";
            console.error("[ScheduleMeetingDialog] Verify failed:", errorMsg);
            setStatusMessage({ 
              text: `✗ Verification failed: ${errorMsg}`, 
              type: "error" 
            });
            toast.error(`Verification failed: ${errorMsg}`);
            
            await supabase.from("error_logs").insert([{
              error_message: "Proposal verification failed",
              error_details: errorMsg,
              component: "scheduling_verification",
              action: "verify_slots",
              metadata: {
                threadId,
                mentorshipRequestId,
                meetingId,
                proposalId,
                verifyResult,
              },
            }]);
            return; // Don't close dialog
          } else {
            // Verification succeeded!
            const slotCount = verifyResult.slot_count || 0;
            const verifiedMeetingId = verifyResult.meeting_id || meetingId;
            const status = verifyResult.status || "unknown";
            
            if (slotCount === 0) {
              setStatusMessage({ 
                text: `✗ Proposal verified but NO SLOTS found (expected ${formattedSlots.length}). Backend RPC bug.`, 
                type: "error" 
              });
              toast.error("Proposal created but no slots inserted. This is a backend RPC bug.");
              
              await supabase.from("error_logs").insert([{
                error_message: "Proposal created but no slots inserted",
                error_details: `Expected ${formattedSlots.length} slots, verify_proposal_slots found 0`,
                component: "scheduling_verification",
                action: "verify_slots",
                metadata: {
                  threadId,
                  mentorshipRequestId,
                  meetingId,
                  proposalId,
                  expectedSlots: formattedSlots.length,
                  verifyResult,
                },
              }]);
              return; // Don't close dialog
            } else {
              // All checks passed!
              setStatusMessage({ 
                text: `✓ All verified! Slots: ${slotCount}, Meeting: ${verifiedMeetingId.slice(0, 8)}..., Status: ${status}`, 
                type: "success" 
              });
              console.log("[ScheduleMeetingDialog] Full verification passed:", verifyResult);
            }
          }
        } catch (verifyError) {
          console.error("[ScheduleMeetingDialog] Verification exception:", verifyError);
          const errMsg = verifyError instanceof Error ? verifyError.message : String(verifyError);
          setStatusMessage({ 
            text: `⚠ Verification exception: ${errMsg}`, 
            type: "error" 
          });
          
          await supabase.from("error_logs").insert([{
            error_message: "Verification exception",
            error_details: errMsg,
            component: "scheduling_verification",
            action: "verify_slots",
            metadata: {
              threadId,
              mentorshipRequestId,
              meetingId,
              proposalId,
            },
          }]);
        }
      } else {
        // Couldn't get proposal_id from RPC response
        setStatusMessage({ 
          text: `⚠ RPC did not return proposal_id. Meeting: ${meetingId === "unknown" ? "unknown" : meetingId.slice(0, 8)}..., Slots reported: ${slotsInserted}`, 
          type: "info" 
        });
        
        await supabase.from("error_logs").insert([{
          error_message: "RPC did not return proposal_id",
          error_details: "create_scheduled_meeting_proposal should return proposal_id",
          component: "scheduling_verification",
          action: "verify_slots",
          metadata: {
            threadId,
            mentorshipRequestId,
            meetingId,
            slotsInserted,
            result,
          },
        }]);
      }
      
      // Keep success visible for a moment before closing
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      onOpenChange(false);
      resetForm();
    } catch (error) {
      toast.dismiss(loadingToastId);
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("[ScheduleMeetingDialog] Proposal creation failed:", error);
      
      // STEP 3b: Show error with specific reason
      setStatusMessage({ text: `✗ Error: ${errorMessage}`, type: "error" });
      
      // Set inline error
      setSubmitError(errorMessage);
      
      // Show error toast with specific message
      toast.error(`Failed to propose meeting: ${errorMessage}`);
      
      // Log to error_logs table
      try {
        await supabase.from("error_logs").insert({
          error_message: "Scheduled meeting proposal creation failed",
          error_details: errorMessage,
          component: "ScheduleMeetingDialog",
          action: "create_scheduled_meeting_proposal",
          metadata: {
            threadId,
            mentorshipRequestId,
            timezone,
            durationMinutes,
            slotsCount: formattedSlots.length,
            rpcPayload,
          },
        });
        console.log("[ScheduleMeetingDialog] Error logged to error_logs table");
      } catch (logErr) {
        console.error("[ScheduleMeetingDialog] Failed to log error:", logErr);
      }
    }
  };

  // Get the debug payload for admin preview
  const getDebugPayload = () => {
    const durationMinutes = parseInt(duration);
    const formattedSlots = slots.map((slot) => ({
      start_at: slot.date.toISOString(),
      end_at: addMinutes(slot.date, durationMinutes).toISOString(),
    }));
    
    return {
      p_mentorship_request_id: mentorshipRequestId,
      p_thread_id: threadId,
      p_duration_minutes: durationMinutes,
      p_timezone: timezone,
      p_note: note.trim() || null,
      p_slots: formattedSlots,
    };
  };

  const resetForm = () => {
    setDuration("30");
    setTimezone("America/Jamaica");
    setNote("");
    setSlots([]);
    setSelectedDate(undefined);
    setSelectedTime("");
    setSubmitError(null);
    setShowDebugPayload(false);
    setStatusMessage(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            Schedule a Meeting
          </DialogTitle>
          <DialogDescription>
            Propose time slots for a meeting with {otherPersonName}. They can accept one of your proposed times or suggest alternatives.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Duration */}
          <div className="space-y-2">
            <Label>Meeting Duration</Label>
            <RadioGroup
              value={duration}
              onValueChange={setDuration}
              className="flex flex-wrap gap-3"
            >
              {DURATION_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={opt.value} id={`dur-${opt.value}`} />
                  <Label htmlFor={`dur-${opt.value}`} className="font-normal cursor-pointer">
                    {opt.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Timezone */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1">
              <Globe className="h-4 w-4" />
              Timezone
            </Label>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONE_OPTIONS.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {timezoneLabel(tz)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Time Slot Selection */}
          <div className="space-y-3">
            <Label>Propose Time Slots (2-5 recommended)</Label>
            
            {/* Stack on mobile, side-by-side on larger screens */}
            <div className="flex flex-col gap-4">
              {/* Calendar */}
              <div className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => isBefore(date, startOfToday())}
                  className="rounded-md border pointer-events-auto"
                />
              </div>
              
              {/* Time selection and add button */}
              <div className="space-y-3">
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px] z-50">
                    {TIME_OPTIONS.map((time) => (
                      <SelectItem key={time.value} value={time.value}>
                        {time.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={handleAddSlot}
                  disabled={!canAddSlot}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Time Slot
                </Button>
                
                {/* Validation feedback */}
                {validationError && selectedDate && selectedTime && (
                  <p className="text-xs text-destructive text-center">
                    {validationError}
                  </p>
                )}
                
                {slots.length > 0 && (
                  <div className="text-xs text-muted-foreground text-center">
                    {slots.length}/5 slots added
                  </div>
                )}
              </div>
            </div>

            {/* Selected Slots */}
            {slots.length > 0 && (
              <div className="space-y-2 mt-4">
                <Label className="text-sm text-muted-foreground">Proposed Times:</Label>
                <div className="flex flex-wrap gap-2">
                  {slots
                    .sort((a, b) => a.date.getTime() - b.date.getTime())
                    .map((slot) => (
                      <Badge
                        key={slot.id}
                        variant="secondary"
                        className="py-1.5 px-3 gap-2"
                      >
                        <Clock className="h-3 w-3" />
                        {format(slot.date, "EEE, MMM d 'at' h:mm a")}
                        <button
                          onClick={() => handleRemoveSlot(slot.id)}
                          className="ml-1 hover:text-destructive"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="schedule-note">Note (Optional)</Label>
            <Textarea
              id="schedule-note"
              placeholder="Add any context about what you'd like to discuss..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="resize-none"
              rows={2}
            />
          </div>

          {/* Error display */}
          {submitError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {submitError}
              </AlertDescription>
            </Alert>
          )}

          {/* Debug payload preview (for admins/debugging) */}
          {slots.length > 0 && (
            <div className="border-t pt-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs text-muted-foreground"
                onClick={() => setShowDebugPayload(!showDebugPayload)}
              >
                <Bug className="h-3 w-3 mr-1" />
                {showDebugPayload ? "Hide" : "Show"} RPC Payload
              </Button>
              {showDebugPayload && (
                <pre className="mt-2 p-2 bg-muted text-xs rounded overflow-x-auto max-h-40">
                  {JSON.stringify(getDebugPayload(), null, 2)}
                </pre>
              )}
            </div>
          )}

          <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
            <p>
              <strong>Note:</strong> The Zoom meeting will be created after {otherPersonName} accepts one of your proposed times.
            </p>
          </div>
        </div>

        {/* User-visible status feedback banner */}
        {statusMessage && (
          <div
            className={`rounded-lg p-4 text-center font-medium text-base ${
              statusMessage.type === "info"
                ? "bg-blue-100 text-blue-800 border border-blue-300"
                : statusMessage.type === "loading"
                ? "bg-yellow-100 text-yellow-800 border border-yellow-300"
                : statusMessage.type === "success"
                ? "bg-green-100 text-green-800 border border-green-300"
                : "bg-red-100 text-red-800 border border-red-300"
            }`}
          >
            {statusMessage.text}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createProposal.isPending || slots.length < 1}
          >
            {createProposal.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CalendarIcon className="h-4 w-4 mr-2" />
            )}
            Propose {slots.length} Time{slots.length !== 1 ? "s" : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
